import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase-config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../assets/styles/AdminShared.css';

const ACTION_PRESETS = [
  { name: 'Approve Project and Pay Deposit', link: '/client/project-acceptance' },
  { name: 'Complete Process Audit', link: '/client/process-audit' },
  { name: 'Complete Brand Questionnaire', link: '/client/brand-questionnaire' },
  { name: 'Provide Website Content', link: '/client/web-content' },
  { name: 'Upload Images and Assets', link: '/client/upload' },
  { name: 'Custom Domain Set Up', link: '/client/domain' },
  { name: 'Home Page Revision', link: '/client/homepage-revision', locked: true },
  { name: 'Website Pages Revision', link: '/client/website-revision', locked: true },
  { name: 'Client Portal Revision', link: '/client/client-portal-revision', locked: true },
  { name: 'Admin Portal Revision', link: '/client/admin-portal-revision', locked: true },
  { name: 'Testing & QA', link: '/client/testing-qa', locked: true },
  { name: 'Launch', link: '/client/launch', locked: true },
  { name: 'Manage Care Plan Requests', link: '/client/care-plan-request', locked: true },
];

const ACTION_LINK_OPTIONS = [
  { label: 'No client page', value: '' },
  { label: 'Project Acceptance', value: '/client/project-acceptance' },
  { label: 'Process Audit', value: '/client/process-audit' },
  { label: 'Brand Questionnaire', value: '/client/brand-questionnaire' },
  { label: 'Website Content', value: '/client/web-content' },
  { label: 'Uploads', value: '/client/upload' },
  { label: 'Custom Domain', value: '/client/domain' },
  { label: 'Home Page Revision', value: '/client/homepage-revision' },
  { label: 'Website Revision', value: '/client/website-revision' },
  { label: 'Client Portal Revision', value: '/client/client-portal-revision' },
  { label: 'Admin Portal Revision', value: '/client/admin-portal-revision' },
  { label: 'Testing & QA', value: '/client/testing-qa' },
  { label: 'Launch', value: '/client/launch' },
  { label: 'Care Plan Requests', value: '/client/care-plan-request' },
];

const ManageProject = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStage, setCurrentStage] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [status, setStatus] = useState('');
  const [carePlanStatus, setCarePlanStatus] = useState('not-offered');
  const [carePlanType, setCarePlanType] = useState('');
  const [customActionName, setCustomActionName] = useState('');
  const [customActionLink, setCustomActionLink] = useState('');
  const [draggedActionIndex, setDraggedActionIndex] = useState(null);
  const [customStageName, setCustomStageName] = useState('');
  const [draggedStageIndex, setDraggedStageIndex] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectRef = doc(db, 'projects', projectId);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const data = projectSnap.data();
          setProject(data);
          setCurrentStage(data.currentStage || 0);
          setStatus(data.status || '');
          setCarePlanStatus(data.carePlanStatus || 'not-offered');
          setCarePlanType(data.carePlanType || '');
          if (data.clientEmail) {
            setInviteLink(`${window.location.origin}/signup?email=${encodeURIComponent(data.clientEmail)}&projectId=${projectId}`);
          }
        } else {
          setError('Project not found');
        }
      } catch (err) {
        setError('Failed to load project: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  const handleStatusUpdate = async () => {
    if (!status) { toast.error('Please enter a status message.'); return; }
    try {
      await updateDoc(doc(db, 'projects', projectId), { status });
      toast.success('Project status updated successfully.');
    } catch (err) {
      toast.error('Failed to update project status: ' + err.message);
    }
  };

  const handleStageUpdate = async (newStage) => {
    setCurrentStage(newStage);
    try {
      const updatedActions = [...project.actions];
      const unlockableStageActions = {
        "Review": "Website Revision",
        "Revision 2": "Home Page Revision",
        "Revision Round 1": "Home Page Revision",
        "Revision Round 2": "Website Revision",
        "Revisions / Bug Fixes": "Testing & QA",
        "Final Revision": "Website Revision",
        "Launch": "Launch", "Launch & Handover": "Launch",
        "Client Portal Revision": "Client Portal Revision", "Admin Portal Revision": "Admin Portal Revision",
        "Client Testing": "Testing & QA",
        "Testing & QA": "Testing & QA", "Home Page Revision": "Home Page Revision",
        "Website Pages Revision": "Website Pages Revision",
        "Care Plan Setup": "Manage Care Plan Requests",
        "Care Plan Plus Setup": "Manage Care Plan Requests",
        "Active Support": "Manage Website Update Requests",
      };
      const stageName = project.stages[newStage].name;
      const actionToUnlock = unlockableStageActions[stageName];
      if (actionToUnlock) {
        const actionIndex = updatedActions.findIndex(a => a.name === actionToUnlock);
        if (actionIndex !== -1) updatedActions[actionIndex].locked = false;
      }
      await updateDoc(doc(db, 'projects', projectId), { currentStage: newStage, actions: updatedActions });
      setProject(prev => ({ ...prev, currentStage: newStage, actions: updatedActions }));
      toast.success('Project stage updated successfully.');
    } catch (err) {
      toast.error('Failed to update project stage: ' + err.message);
    }
  };

  const normalizeStage = (stage) => (
    typeof stage === 'string'
      ? { name: stage, start: '', end: '' }
      : { name: stage?.name || '', start: stage?.start || '', end: stage?.end || '' }
  );

  const saveProjectStages = async (updatedStages, successMessage = 'Project progress updated.') => {
    const safeCurrentStage = Math.min(currentStage, Math.max(updatedStages.length - 1, 0));

    try {
      await updateDoc(doc(db, 'projects', projectId), { stages: updatedStages, currentStage: safeCurrentStage });
      setCurrentStage(safeCurrentStage);
      setProject(prev => ({ ...prev, stages: updatedStages, currentStage: safeCurrentStage }));
      toast.success(successMessage);
    } catch (err) {
      toast.error('Failed to update project progress: ' + err.message);
    }
  };

  const updateStageDraft = (stageIndex, field, value) => {
    const updatedStages = [...(project.stages || [])].map(normalizeStage);
    updatedStages[stageIndex] = { ...updatedStages[stageIndex], [field]: value };
    setProject(prev => ({ ...prev, stages: updatedStages }));
  };

  const handleSaveStages = () => {
    const cleanedStages = (project.stages || [])
      .map(normalizeStage)
      .map(stage => ({
        name: stage.name.trim(),
        start: stage.start || '',
        end: stage.end || '',
      }))
      .filter(stage => stage.name);

    saveProjectStages(cleanedStages);
  };

  const handleRemoveStage = (stageIndex) => {
    const updatedStages = (project.stages || []).map(normalizeStage).filter((_, index) => index !== stageIndex);
    saveProjectStages(updatedStages, 'Progress step removed.');
  };

  const handleStageDrop = (dropIndex) => {
    if (draggedStageIndex === null || draggedStageIndex === dropIndex) {
      setDraggedStageIndex(null);
      return;
    }

    const updatedStages = [...(project.stages || [])].map(normalizeStage);
    const [movedStage] = updatedStages.splice(draggedStageIndex, 1);
    updatedStages.splice(dropIndex, 0, movedStage);

    setDraggedStageIndex(null);
    saveProjectStages(updatedStages, 'Progress order updated.');
  };

  const handleAddStage = () => {
    const name = customStageName.trim();
    if (!name) {
      toast.error('Enter a progress step name first.');
      return;
    }

    saveProjectStages([...(project.stages || []).map(normalizeStage), { name, start: '', end: '' }], 'Progress step added.');
    setCustomStageName('');
  };

  const toggleActionComplete = async (actionIndex) => {
    if (!project) return;
    const updatedActions = [...project.actions];
    updatedActions[actionIndex].complete = !updatedActions[actionIndex].complete;
    try {
      await updateDoc(doc(db, 'projects', projectId), { actions: updatedActions });
      setProject(prev => ({ ...prev, actions: updatedActions }));
    } catch (err) {
      setError('Failed to update action status: ' + err.message);
    }
  };

  const saveProjectActions = async (updatedActions, successMessage = 'Project actions updated.') => {
    try {
      await updateDoc(doc(db, 'projects', projectId), { actions: updatedActions });
      setProject(prev => ({ ...prev, actions: updatedActions }));
      toast.success(successMessage);
    } catch (err) {
      toast.error('Failed to update project actions: ' + err.message);
    }
  };

  const updateActionDraft = (actionIndex, field, value) => {
    const updatedActions = [...(project.actions || [])];
    updatedActions[actionIndex] = { ...updatedActions[actionIndex], [field]: value };
    setProject(prev => ({ ...prev, actions: updatedActions }));
  };

  const handleSaveActions = () => {
    const cleanedActions = (project.actions || [])
      .map(action => ({
        name: (action.name || '').trim(),
        link: (action.link || '').trim(),
        complete: Boolean(action.complete),
        locked: Boolean(action.locked),
      }))
      .filter(action => action.name);

    saveProjectActions(cleanedActions);
  };

  const handleRemoveAction = (actionIndex) => {
    const updatedActions = (project.actions || []).filter((_, index) => index !== actionIndex);
    saveProjectActions(updatedActions, 'Action removed.');
  };

  const handleActionDrop = (dropIndex) => {
    if (draggedActionIndex === null || draggedActionIndex === dropIndex) {
      setDraggedActionIndex(null);
      return;
    }

    const updatedActions = [...(project.actions || [])];
    const [movedAction] = updatedActions.splice(draggedActionIndex, 1);
    updatedActions.splice(dropIndex, 0, movedAction);

    setDraggedActionIndex(null);
    saveProjectActions(updatedActions, 'Action order updated.');
  };

  const handleAddAction = (preset) => {
    const action = {
      name: preset.name,
      link: preset.link || '',
      complete: false,
      locked: Boolean(preset.locked),
    };

    saveProjectActions([...(project.actions || []), action], 'Action added.');
  };

  const handleAddCustomAction = () => {
    const name = customActionName.trim();
    if (!name) {
      toast.error('Enter an action name first.');
      return;
    }

    handleAddAction({ name, link: customActionLink });
    setCustomActionName('');
    setCustomActionLink('');
  };

  const handleBrandStrategyUpload = async (file) => {
    if (!file) { toast.error('No file selected.'); return; }
    const storageRef = ref(storage, `projects/${projectId}/brandStrategy/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on('state_changed', null, () => toast.error('Failed to upload file. Please try again.'), async () => {
      try {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        await updateDoc(doc(db, 'projects', projectId), { 'projectFiles.brandStrategy': downloadURL });
        setProject(prev => ({ ...prev, projectFiles: { ...prev.projectFiles, brandStrategy: downloadURL } }));
        toast.success('Brand strategy uploaded successfully.');
      } catch {
        toast.error('Failed to save file metadata. Please try again.');
      }
    });
  };

  const handleUpdateProject = async (field, value) => {
    try {
      await updateDoc(doc(db, 'projects', projectId), { [field]: value });
      setProject(prev => ({ ...prev, [field]: value }));
      toast.success(`${field} updated successfully.`);
    } catch (err) {
      toast.error('Failed to update project: ' + err.message);
    }
  };

  const handleCarePlanUpdate = async () => {
    if (!project) return;

    const isActive = carePlanStatus === 'active';
    const actionName = project.packageKey === 'care-plan'
      ? 'Manage Website Update Requests'
      : 'Manage Care Plan Requests';
    const existingActions = (project.actions || []).filter(action => action.name !== 'Review Monthly Support Status');
    const hasCareRequestAction = existingActions.some(action => action.link === '/client/care-plan-request');
    const updatedActions = hasCareRequestAction
      ? existingActions.map(action =>
          action.link === '/client/care-plan-request'
            ? { ...action, name: action.name || actionName, locked: !isActive }
            : action
        )
      : [
          ...existingActions,
          { name: actionName, link: '/client/care-plan-request', complete: false, locked: !isActive },
        ];

    const updatedFields = {
      carePlanStatus,
      carePlanType: carePlanType || null,
      carePlanOffered: carePlanStatus !== 'not-offered',
      actions: updatedActions,
    };

    try {
      await updateDoc(doc(db, 'projects', projectId), updatedFields);
      setProject(prev => ({ ...prev, ...updatedFields }));
      toast.success('Care Plan setup updated.');
    } catch (err) {
      toast.error('Failed to update Care Plan setup: ' + err.message);
    }
  };

  const handleDeleteProject = async () => {
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      setShowDeleteModal(false);
      navigate('/admin');
      toast.success('Project deleted successfully.');
    } catch (err) {
      toast.error('Failed to delete project: ' + err.message);
    }
  };

  const getActionRoute = (action) => {
    const routes = {
      "Home Page Revision": `/admin/project/${projectId}/homepage-revision`,
      "Website Revision": `/admin/project/${projectId}/website-revision`,
      "Website Pages Revision": `/admin/project/${projectId}/website-revision`,
      "Client Portal Revision": `/admin/project/${projectId}/client-portal-revision`,
      "Admin Portal Revision": `/admin/project/${projectId}/admin-portal-revision`,
      "Testing & QA": `/admin/project/${projectId}/testing-qa`,
      "Launch": `/admin/project/${projectId}/launch`,
      "Custom Domain Set Up": `/admin/project/${projectId}/domain`,
    };
    if (action.link === '/client/starter-intake') return `/admin/project/${projectId}/starter-intake`;
    if (action.link === '/client/project-acceptance') return `/proposal/${projectId}`;
    if (action.link === '/client/care-plan-request') return `/admin/project/${projectId}/care-plan-requests`;
    if (action.link === '/client/growth-intake') return `/admin/project/${projectId}/growth-intake`;
    if (action.link === '/client/process-audit') return `/admin/project/${projectId}/process-audit`;
    if (action.link === '/client/brand-questionnaire') return `/admin/project/${projectId}/brand-questionnaire`;
    if (action.link === '/client/web-content') return `/admin/project/${projectId}/website-content`;
    if (action.link === '/client/upload') return `/admin/project/${projectId}/uploaded-pictures`;
    return routes[action.name] || null;
  };

  if (loading) return <div className="ap-loading"><p>Loading project...</p></div>;
  if (error) return <div className="ap-loading ap-loading--error"><p>{error}</p></div>;

  return (
    <div className="ap-page">
      {/* Page header */}
      <div className="ap-page-header">
        <a href="/admin" className="ap-back">← Back to dashboard</a>
        <div className="ap-page-header-row">
          <div>
            <p className="ap-eyebrow">Admin — Project</p>
            <h1 className="ap-title">{project?.projectName}</h1>
          </div>
          <button className="ap-btn-danger" onClick={() => setShowDeleteModal(true)}>
            Delete Project
          </button>
        </div>
      </div>

      {/* Meta strip */}
      <div className="ap-meta-row">
        <div className="ap-meta-item">
          <span className="ap-meta-label">Project ID</span>
          <span className="ap-meta-value" style={{ fontSize: '0.8125rem', fontFamily: 'var(--font-mono)' }}>{projectId}</span>
        </div>
        <div className="ap-meta-item">
          <span className="ap-meta-label">Package</span>
          <span className="ap-meta-value">{project?.packageType}</span>
        </div>
        <div className="ap-meta-item">
          <span className="ap-meta-label">Client Email</span>
          <span className="ap-meta-value">{project?.clientEmail}</span>
        </div>
      </div>

      {/* Progress editor */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Project Progress Setup</h2>
          <span className="ap-badge ap-badge--pending">{project?.stages?.length || 0} Steps</span>
        </div>
        <div className="ap-section-body">
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-tertiary)', lineHeight: 1.6, margin: '0 0 16px' }}>
            Edit the major project phases shown in the client portal. These should be broad milestones, not every engineering task.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '18px' }}>
            {(project.stages || []).map((stage, index) => {
              const normalizedStage = normalizeStage(stage);
              return (
                <div
                  key={`${normalizedStage.name || 'stage'}-${index}`}
                  className={`ap-list-item ap-draggable-action${draggedStageIndex === index ? ' dragging' : ''}`}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => handleStageDrop(index)}
                  style={{ alignItems: 'stretch', gap: '12px' }}
                >
                  <span
                    className="ap-drag-handle"
                    title="Drag to reorder"
                    aria-label={`Drag ${normalizedStage.name || 'progress step'} to reorder`}
                    draggable
                    onDragStart={() => setDraggedStageIndex(index)}
                    onDragEnd={() => setDraggedStageIndex(null)}
                  >
                    ⋮⋮
                  </span>
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'minmax(180px, 1.6fr) minmax(130px, 0.7fr) minmax(130px, 0.7fr) auto', gap: '12px', alignItems: 'center' }}>
                    <div className="ap-form-group" style={{ margin: 0 }}>
                      <label>Step Name</label>
                      <input
                        type="text"
                        value={normalizedStage.name}
                        onChange={e => updateStageDraft(index, 'name', e.target.value)}
                      />
                    </div>
                    <div className="ap-form-group" style={{ margin: 0 }}>
                      <label>Start</label>
                      <input
                        type="date"
                        value={normalizedStage.start}
                        onChange={e => updateStageDraft(index, 'start', e.target.value)}
                      />
                    </div>
                    <div className="ap-form-group" style={{ margin: 0 }}>
                      <label>End</label>
                      <input
                        type="date"
                        value={normalizedStage.end}
                        onChange={e => updateStageDraft(index, 'end', e.target.value)}
                      />
                    </div>
                    <button className="ap-btn-danger" style={{ padding: '8px 12px', alignSelf: 'end' }} onClick={() => handleRemoveStage(index)}>
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ap-btn-row" style={{ marginTop: 0, marginBottom: '18px' }}>
            <button className="ap-btn-primary" onClick={handleSaveStages}>Save Progress Setup</button>
          </div>

          <div className="ap-form-grid">
            <div className="ap-form-group">
              <label>New Progress Step</label>
              <input
                type="text"
                value={customStageName}
                onChange={e => setCustomStageName(e.target.value)}
                placeholder="Example: Results Dashboard"
              />
            </div>
          </div>
          <div className="ap-btn-row" style={{ marginTop: 0 }}>
            <button className="ap-btn-secondary" onClick={handleAddStage}>Add Progress Step</button>
          </div>
        </div>
      </div>

      {/* Progress tracker */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Project Progress</h2>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
            Stage {currentStage + 1} of {project?.stages?.length}
          </span>
        </div>
        <div className="ap-progress-wrap">
          <div className="ap-progress">
            {project?.stages?.map((stage, index) => (
              <div
                key={index}
                className={`ap-progress-step${index < currentStage ? ' done' : index === currentStage ? ' current' : ''}`}
                onClick={() => handleStageUpdate(index)}
              >
                <div className="ap-progress-dot" />
                <div className="ap-progress-label">{stage.name || stage}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Client acceptance */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Client Acceptance</h2>
          {project?.projectAccepted
            ? <span className="ap-badge ap-badge--active">Accepted</span>
            : <span className="ap-badge ap-badge--pending">Pending</span>
          }
        </div>
        <div className="ap-section-body">
          <div className="ap-meta-row" style={{ marginBottom: project?.clientAcceptance?.notes ? '16px' : 0 }}>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Contract</span>
              <span className="ap-meta-value">{project?.contractSigned ? 'Signed' : 'Not confirmed'}</span>
            </div>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Deposit</span>
              <span className="ap-meta-value">{project?.depositPaid ? 'Paid' : 'Not confirmed'}</span>
            </div>
            <div className="ap-meta-item">
              <span className="ap-meta-label">Completed At</span>
              <span className="ap-meta-value">{project?.clientAcceptance?.completedAt ? new Date(project.clientAcceptance.completedAt).toLocaleDateString() : 'Pending'}</span>
            </div>
          </div>
          {project?.clientAcceptance?.notes && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>
              {project.clientAcceptance.notes}
            </p>
          )}
        </div>
      </div>

      {/* Care plan setup */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Care Plan Setup</h2>
          <span className={`ap-badge ${project?.carePlanStatus === 'active' ? 'ap-badge--active' : 'ap-badge--pending'}`}>
            {project?.carePlanStatus || 'not-offered'}
          </span>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-grid">
            <div className="ap-form-group">
              <label>Care Plan Status</label>
              <select value={carePlanStatus} onChange={e => setCarePlanStatus(e.target.value)}>
                <option value="not-offered">Not offered</option>
                <option value="offered">Offered</option>
                <option value="pending-subscription">Pending subscription</option>
                <option value="active">Active</option>
                <option value="declined">Declined</option>
                <option value="paused">Paused</option>
              </select>
            </div>
            <div className="ap-form-group">
              <label>Care Plan Type</label>
              <select value={carePlanType} onChange={e => setCarePlanType(e.target.value)}>
                <option value="">None selected</option>
                <option value="basic">Basic - $149/mo</option>
                <option value="plus">Plus - $199/mo</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-tertiary)', lineHeight: 1.6, margin: '0 0 16px' }}>
            Setting the plan to Active unlocks the client's Care Plan request action. This also adds the request action to older emulator projects that do not have it yet.
          </p>
          <div className="ap-list-item" style={{ alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                Basic - $149/mo
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                Best for Starter/Growth brochure sites. Covers backups, uptime monitoring, priority email support, and up to 1 hour of small edits per month.
              </p>
            </div>
          </div>
          <div className="ap-list-item" style={{ alignItems: 'flex-start', marginBottom: '16px' }}>
            <div>
              <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                Plus - $199/mo
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                Best for Authority/custom builds. Includes Basic plus checks for custom forms, portals, workflows, Firebase data, and small bug fixes for functionality Next Level Websites built.
              </p>
            </div>
          </div>
          <div className="ap-btn-row">
            <button className="ap-btn-primary" onClick={handleCarePlanUpdate}>Save Care Plan Setup</button>
          </div>
        </div>
      </div>

      {/* Care plan requests */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Care Plan Requests</h2>
          <span className="ap-badge ap-badge--pending">{project?.carePlanRequests?.length || 0} Total</span>
        </div>
        <div className="ap-section-body">
          <div className="ap-btn-row" style={{ marginTop: 0, marginBottom: project?.carePlanRequests?.length ? '16px' : 0 }}>
            <button className="ap-btn-primary" onClick={() => navigate(`/admin/project/${projectId}/care-plan-requests`)}>
              Manage Requests
            </button>
          </div>
          {project?.carePlanRequests?.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {project.carePlanRequests.slice().reverse().map((request, index) => (
                <div key={`${request.submittedAt || index}-${index}`} className="ap-list-item" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px' }}>
                      {request.requestType} · {request.priority}
                    </p>
                    <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-tertiary)', margin: '0 0 8px' }}>
                      {request.pageOrFeature || 'No page specified'} {request.deadline ? `· Deadline: ${request.deadline}` : ''}
                    </p>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                      {request.details}
                    </p>
                  </div>
                  <span className="ap-badge ap-badge--pending">{request.status || 'new'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.9375rem', color: 'var(--text-tertiary)', margin: 0 }}>
              No Care Plan requests submitted yet.
            </p>
          )}
        </div>
      </div>

      {/* Action editor */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Project Action Setup</h2>
          <span className="ap-badge ap-badge--pending">{project?.actions?.length || 0} Actions</span>
        </div>
        <div className="ap-section-body">
          <p style={{ fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-tertiary)', lineHeight: 1.6, margin: '0 0 16px' }}>
            Choose the client/admin workflow actions for this project. This is useful for Custom projects where the default Authority action list is too much.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '18px' }}>
            {(project.actions || []).map((action, index) => (
              <div
                key={`${action.name || 'action'}-${index}`}
                className={`ap-list-item ap-draggable-action${draggedActionIndex === index ? ' dragging' : ''}`}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleActionDrop(index)}
                style={{ alignItems: 'stretch', gap: '12px' }}
              >
                <span
                  className="ap-drag-handle"
                  title="Drag to reorder"
                  aria-label={`Drag ${action.name || 'action'} to reorder`}
                  draggable
                  onDragStart={() => setDraggedActionIndex(index)}
                  onDragEnd={() => setDraggedActionIndex(null)}
                >
                  ⋮⋮
                </span>
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'minmax(180px, 1.5fr) minmax(180px, 1fr) auto', gap: '12px', alignItems: 'center' }}>
                  <div className="ap-form-group" style={{ margin: 0 }}>
                    <label>Action Name</label>
                    <input
                      type="text"
                      value={action.name || ''}
                      onChange={e => updateActionDraft(index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="ap-form-group" style={{ margin: 0 }}>
                    <label>Client Page</label>
                    <select value={action.link || ''} onChange={e => updateActionDraft(index, 'link', e.target.value)}>
                      {ACTION_LINK_OPTIONS.map(option => (
                        <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'var(--font-ui)', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(action.locked)}
                        onChange={e => updateActionDraft(index, 'locked', e.target.checked)}
                      />
                      Locked
                    </label>
                    <button className="ap-btn-danger" style={{ padding: '8px 12px' }} onClick={() => handleRemoveAction(index)}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="ap-btn-row" style={{ marginTop: 0, marginBottom: '18px' }}>
            <button className="ap-btn-primary" onClick={handleSaveActions}>Save Action Setup</button>
          </div>

          <div className="ap-form-grid" style={{ marginBottom: '16px' }}>
            <div className="ap-form-group">
              <label>Add Preset Action</label>
              <select
                defaultValue=""
                onChange={e => {
                  const preset = ACTION_PRESETS.find(option => option.name === e.target.value);
                  if (preset) handleAddAction(preset);
                  e.target.value = '';
                }}
              >
                <option value="">Select an action to add</option>
                {ACTION_PRESETS.map(action => (
                  <option key={action.name} value={action.name}>{action.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="ap-form-grid">
            <div className="ap-form-group">
              <label>Custom Action Name</label>
              <input
                type="text"
                value={customActionName}
                onChange={e => setCustomActionName(e.target.value)}
                placeholder="Example: Review AI Results Flow"
              />
            </div>
            <div className="ap-form-group">
              <label>Client Page</label>
              <select value={customActionLink} onChange={e => setCustomActionLink(e.target.value)}>
                {ACTION_LINK_OPTIONS.map(option => (
                  <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="ap-btn-row" style={{ marginTop: 0 }}>
            <button className="ap-btn-secondary" onClick={handleAddCustomAction}>Add Custom Action</button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Actions</h2>
        </div>
        <div className="ap-action-grid">
          {project.actions.map((action, index) => {
            const route = getActionRoute(action);
            return (
              <div key={index} className={`ap-action-card${action.complete ? ' completed' : ''}`}>
                <h4>{action.name}</h4>
                {action.locked && (
                  <span className="ap-badge ap-badge--pending">Locked</span>
                )}
                {route && !action.locked && (
                  <button className="ap-btn-secondary" onClick={() => navigate(route)}>
                    View →
                  </button>
                )}
                <div className="ap-action-complete">
                  <input
                    type="checkbox"
                    id={`action-${index}`}
                    checked={action.complete}
                    onChange={() => toggleActionComplete(index)}
                  />
                  <label htmlFor={`action-${index}`}>
                    {action.complete ? 'Completed' : 'Mark complete'}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status update */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Project Status</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Status Message (visible to client)</label>
            <textarea value={status} onChange={e => setStatus(e.target.value)} placeholder="Write a short update about the project's progress..." />
          </div>
          <div className="ap-btn-row">
            <button className="ap-btn-primary" onClick={handleStatusUpdate}>Update Status</button>
          </div>
        </div>
      </div>

      {/* Invite link */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Client Invite Link</h2>
        </div>
        <div className="ap-section-body">
          {inviteLink ? (
            <div className="ap-link-display">
              <a href={inviteLink} target="_blank" rel="noopener noreferrer">{inviteLink}</a>
            </div>
          ) : (
            <p className="ap-empty" style={{ padding: 0 }}>Client email is missing — cannot generate invite link.</p>
          )}
        </div>
      </div>

      {/* Proposal */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Proposal</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-btn-row">
            <button className="ap-btn-secondary" onClick={() => navigate(`/admin/project/${projectId}/create-proposal`)}>
              Edit Proposal
            </button>
            <button className="ap-btn-secondary" onClick={() => navigate(`/proposal/${projectId}`)}>
              Preview Proposal
            </button>
          </div>
        </div>
      </div>

      {/* Tutorial video */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Client Portal Tutorial Video</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Tutorial Video Link</label>
            <input
              type="url"
              placeholder="Enter tutorial video link"
              value={project?.clientPortalTutorialVideo || ''}
              onChange={e => setProject(prev => ({ ...prev, clientPortalTutorialVideo: e.target.value }))}
            />
          </div>
          <div className="ap-btn-row">
            <button className="ap-btn-primary" onClick={() => handleUpdateProject('clientPortalTutorialVideo', project.clientPortalTutorialVideo)}>
              Save Link
            </button>
          </div>
        </div>
      </div>

      {/* Key dates */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Key Dates</h2>
        </div>
        <div className="ap-section-body">
          {project?.keyDates?.map((keyDate, index) => (
            <div key={index} className="ap-list-item">
              <input
                type="text"
                placeholder="Date name"
                value={keyDate.name || ''}
                onChange={e => {
                  const updated = [...project.keyDates];
                  updated[index].name = e.target.value;
                  setProject(prev => ({ ...prev, keyDates: updated }));
                }}
              />
              <input
                type="date"
                value={keyDate.date || ''}
                onChange={e => {
                  const updated = [...project.keyDates];
                  updated[index].date = e.target.value;
                  setProject(prev => ({ ...prev, keyDates: updated }));
                }}
              />
              <button className="ap-btn-danger" style={{ padding: '8px 12px', fontSize: '0.8125rem' }} onClick={() => {
                const updated = project.keyDates.filter((_, i) => i !== index);
                setProject(prev => ({ ...prev, keyDates: updated }));
              }}>
                Remove
              </button>
            </div>
          ))}
          <div className="ap-btn-row">
            <button className="ap-btn-secondary" onClick={() => setProject(prev => ({ ...prev, keyDates: [...(prev.keyDates || []), { name: '', date: '' }] }))}>
              + Add Date
            </button>
            <button className="ap-btn-primary" onClick={() => handleUpdateProject('keyDates', project.keyDates)}>
              Save Dates
            </button>
          </div>
        </div>
      </div>

      {/* Deliverables */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Deliverables</h2>
        </div>
        <div className="ap-section-body">
          {project?.deliverables?.map((deliverable, index) => (
            <div key={index} className="ap-list-item">
              <input
                type="text"
                value={deliverable}
                onChange={e => {
                  const updated = [...project.deliverables];
                  updated[index] = e.target.value;
                  setProject(prev => ({ ...prev, deliverables: updated }));
                }}
              />
              <button className="ap-btn-danger" style={{ padding: '8px 12px', fontSize: '0.8125rem' }} onClick={() => {
                const updated = project.deliverables.filter((_, i) => i !== index);
                setProject(prev => ({ ...prev, deliverables: updated }));
              }}>
                Remove
              </button>
            </div>
          ))}
          <div className="ap-btn-row">
            <button className="ap-btn-secondary" onClick={() => setProject(prev => ({ ...prev, deliverables: [...(prev.deliverables || []), ''] }))}>
              + Add Deliverable
            </button>
            <button className="ap-btn-primary" onClick={() => handleUpdateProject('deliverables', project.deliverables)}>
              Save Deliverables
            </button>
          </div>
        </div>
      </div>

      {/* Proposal documents */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Proposal Documents</h2>
        </div>
        <div className="ap-section-body">
          {['contract', 'invoice'].map(docType => (
            <div key={docType} className="ap-form-group">
              <label>{docType.charAt(0).toUpperCase() + docType.slice(1)}</label>
              <input
                type="url"
                placeholder={`Enter ${docType} link`}
                value={project?.proposalDocuments?.[docType] || ''}
                onChange={e => handleUpdateProject('proposalDocuments', { ...project.proposalDocuments, [docType]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Project files */}
      <div className="ap-section" style={{ marginBottom: '20px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Project Files</h2>
        </div>
        <div className="ap-section-body">
          <div className="ap-form-group">
            <label>Brand Strategy (PDF)</label>
            <input type="file" accept="application/pdf" onChange={e => { if (e.target.files[0]) handleBrandStrategyUpload(e.target.files[0]); }} />
            {project?.projectFiles?.brandStrategy && (
              <a href={project.projectFiles.brandStrategy} target="_blank" rel="noopener noreferrer" className="ap-row-link" style={{ fontSize: '0.875rem', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
                View Uploaded Brand Strategy →
              </a>
            )}
          </div>
          {['firebaseProject', 'finalFiles'].map(fileType => (
            <div key={fileType} className="ap-form-group">
              <label>{fileType === 'firebaseProject' ? 'Firebase Project' : 'Final Files'}</label>
              <input
                type="text"
                placeholder={`Enter ${fileType} details`}
                value={project?.projectFiles?.[fileType] || ''}
                onChange={e => handleUpdateProject('projectFiles', { ...project.projectFiles, [fileType]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Resources */}
      <div className="ap-section" style={{ marginBottom: '32px' }}>
        <div className="ap-section-header">
          <h2 className="ap-section-title">Resources</h2>
        </div>
        <div className="ap-section-body">
          {['trainingVideo', 'faqs', 'guides'].map(resourceType => (
            <div key={resourceType} className="ap-form-group">
              <label>{resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}</label>
              <input
                type="text"
                placeholder={`Enter ${resourceType} link`}
                value={project?.resources?.[resourceType] || ''}
                onChange={e => handleUpdateProject('resources', { ...project.resources, [resourceType]: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Delete modal */}
      {showDeleteModal && (
        <div className="ap-modal-overlay">
          <div className="ap-modal">
            <h2>Delete this project?</h2>
            <p>This action cannot be undone. All project data will be permanently removed.</p>
            <div className="ap-btn-row">
              <button className="ap-btn-danger" onClick={handleDeleteProject}>Yes, delete</button>
              <button className="ap-btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProject;
